import { getCountryMapping } from '@/application/helpers/languageHelpers'
import { factory } from '../../../../factory-api/factory'
import { defineStore } from 'pinia'
import { useAppStore, useProgrammeStore } from '@/stores'
import { Device, type GetLanguageCodeResult } from '@capacitor/device'
import { extractLanguage, resolveApiUrl } from '../../../application/helpers/index'
import type {
  BrandMapping,
  CountryMapping,
  LanguageMappings,
  IUrl
} from '../../../interfaces/helpers/ILanguage'
import { languageMappings } from '../../../application/helpers/brandMappings'
import type { ILanguageItem } from '../../../interfaces/ILanguageItem'
import { Capacitor } from '@capacitor/core'

export const useLanguageStore = defineStore('language', () => {
  const appStore = useAppStore()
  const programmeStore = useProgrammeStore()
  const listOfAvailableLanguages: Ref<ILanguageItem[] | undefined> = ref(undefined)
  const currentLocale: Ref<string> = ref('')
  const currentCountry: Ref<CountryMapping | undefined> = ref()
  const localeMapping: { [key: string]: string } = {
    Dansk: 'da-DK'
    // Add more mappings as needed
  }
  const currentBrand: Ref<BrandMapping | undefined> = ref()

  async function setListOfAvailableLanguages() {
    try {
      const frontpageAlias = programmeStore.selectedProgramme?.contentSettings?.frontPage
      if (frontpageAlias !== undefined) {
        const contentApi = await factory.getContentApi()
        const response = await contentApi.getLanguagesForDynamicFrontPage({
          documentType: 'DynamicFrontPage',
          property: 'FrontPageAlias',
          pageAlias:
            Capacitor.getPlatform() === 'web' ? frontpageAlias : encodeURIComponent(frontpageAlias)
        })
        mapLanguages(response as string[])
      }
    } catch (error) {
      console.log(error)
    }
  }

  const mapLanguages = (languages: string[]): ILanguageItem[] => {
    // Initialize an array to store mapped language items
    const mappedLanguages = [] as ILanguageItem[]

    // Map through the languages and find corresponding country mappings
    languages.forEach((lang: string) => {
      // Find the country with the matching locale
      const country = languageMappings
        .find((langMap: LanguageMappings) => langMap.brand === appStore.getBrand())
        ?.countries.find((country: CountryMapping) => country.locale === lang)

      // If a country is found, push a language item to the mappedLanguages array
      if (country) {
        mappedLanguages.push({
          locale: lang,
          name: country.language.fullName,
          image: country.flag
        } as ILanguageItem)
      }
    })
    listOfAvailableLanguages.value = mappedLanguages

    // Return the array of mapped language items
    return mappedLanguages
  }

  const setApiBasedOnLanguage = async () => {
    const language: GetLanguageCodeResult = await Device.getLanguageCode()
    console.log('setApiBasedOnLanguage', language.value)
    setApiUrlBasedOnLanguage(language.value)
  }

  const setApiBasedOnSelectedLanguage = (language: string) => {
    console.log('setApiBasedOnSelectedLanguage')
    setApiUrlBasedOnLanguage(language)
    localStorage.setItem('chosenLanguageUrl', appStore.apiUrl)
  }

  const setLanguageBasedOnFocus = (language: string) => {
    console.log('setLanguageBasedOnFocus', language)
    setApiUrlBasedOnLanguage(language)
    localStorage.setItem('chosenLanguageUrl', appStore.apiUrl)
  }

  const setApiBasedOnUrl = async () => {
    const url = window.location.hostname
    const language: CountryMapping | undefined = extractLanguage(url)
    console.log('language countrymap', language)
    if (language != undefined) {
      setApiUrlBasedOnLanguage(language.locale)
    }
  }

  const setApiUrlBasedOnLanguage = (locale: string) => {
    console.log('setApiUrlBasedOnLanguage', locale)
    let countryMap: CountryMapping = getCountryMapping(locale)
    console.log('countryMap ', countryMap, countryMap.envUrlKey)
    if (countryMap === undefined || countryMap.envUrlKey === undefined) {
      countryMap = getCountryMapping('en')
    }

    const newApiUrl: string | undefined = resolveApiUrl(countryMap.envUrlKey)
    setCurrentLocale(locale)
    console.log('newApiUrl resolve to: ', newApiUrl)

    appStore.setApiUrl(newApiUrl)
    setLocalStorageItems(
      appStore.apiUrl,
      locale,
      countryMap.urls.find((url) => url.environment === import.meta.env.VITE_ENVIRONMENT)?.url ?? ''
    )
  }

  const setLocalStorageItems = (url: string, locale: string, appUrl: string) => {
    localStorage.setItem('appLanguageUrl', url)
    localStorage.setItem('appLanguageLocale', locale)
    localStorage.setItem('webAppUrl', appUrl)
  }

  const getWebAppUrl = (locale: string) => {
    const countryMap: CountryMapping = getCountryMapping(locale)
    return countryMap.urls.find((url: IUrl) => url.environment === import.meta.env.VITE_ENVIRONMENT)
      ?.url
  }

  const setCurrentLocale = (locale: string) => {
    // It seems iOS sets locale as "Dansk" so this code helps map it to da-DK - add if we have more examples like this.
    const standardizedLocale = localeMapping[locale] || locale
    console.log('setCurrentLocale', locale, standardizedLocale)
    currentLocale.value = standardizedLocale
    setCountry(locale)
  }

  const setCountry = (locale: string) => {
    // Find the correct CountryMapping - by - first getting the correct brand - second getting the correct CountryMappen from current Locale
    const brand = appStore.getBrand()
    for (const language in languageMappings) {
      if (brand === languageMappings[language].brand) {
        currentBrand.value = languageMappings[language]
        for (const country in currentBrand.value.countries) {
          const tempCountry: CountryMapping = currentBrand.value.countries[country]
          if (locale && tempCountry.locales.some((lo: string) => lo === locale)) {
            currentCountry.value = tempCountry
          }
        }
      }
    }
  }

  const resetLanguageStore = () => {
    listOfAvailableLanguages.value = undefined
  }

  const businessCountryLocale = () => {
    const programmeStore = useProgrammeStore()

    if (programmeStore.selectedProgramme !== undefined) {
      const businessCountry = programmeStore.selectedProgramme.businessCountry
      const langStore = useLanguageStore()
      // Get the current brand to extract the correct locale
      const getBusinessCountry = langStore.currentBrand?.countries.find(
        (c) => c.country === businessCountry?.toLowerCase()
      )
      if (getBusinessCountry?.locale !== undefined) {
        return getBusinessCountry?.locale // This will be i.e. 'da-DK'.
      }
    }
    // return a default if we cant find a selectedProgramme
    return 'da-DK'
  }

  return {
    listOfAvailableLanguages,
    setListOfAvailableLanguages,
    setApiBasedOnLanguage,
    setApiBasedOnSelectedLanguage,
    setLanguageBasedOnFocus,
    setApiBasedOnUrl,
    setApiUrlBasedOnLanguage,
    setCurrentLocale,
    currentLocale,
    resetLanguageStore,
    currentCountry,
    currentBrand,
    getWebAppUrl,
    setLocalStorageItems,
    businessCountryLocale
  }
})
